import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    this.#dialogEl = this.querySelector('[ref="dialog"]');
    this.#setHeaderHeightVar();
    this.#setFooterHeightVar();
    window.addEventListener('resize', this.#resizeHandler, false);
    window.addEventListener('scroll', this.#scrollHandler, false);
    window.addEventListener('orientationchange', this.#resizeHandler, false);
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    // Close drawer when clicking outside of the inner panel
    document.addEventListener('click', this.#handleDocumentClick, true);
    // Close on Escape
    document.addEventListener('keydown', this.#handleKeydown, false);
    // Listen for temporary lock-open requests
    this.addEventListener('cart-drawer:lock-open', this.#handleLockOpen);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeHandler, false);
    window.removeEventListener('scroll', this.#scrollHandler, false);
    window.removeEventListener('orientationchange', this.#resizeHandler, false);
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('click', this.#handleDocumentClick, true);
    document.removeEventListener('keydown', this.#handleKeydown, false);
    this.removeEventListener('cart-drawer:lock-open', this.#handleLockOpen);
  }

  /** @type {HTMLDialogElement|null} */
  #dialogEl = null;

  #resizeHandler = () => {
    this.#setHeaderHeightVar();
    this.#setFooterHeightVar();
  };

  #scrollHandler = () => {
    // Throttle updates to once per frame
    if (this.#rafId != null) return;
    this.#rafId = window.requestAnimationFrame(() => {
      this.#rafId = null;
      this.#setHeaderHeightVar();
      this.#setFooterHeightVar();
    });
  };

  /** @type {number|null} */
  #rafId = null;
  /** @type {number} */
  #lastHeaderPx = -1;
  /** @type {number} */
  #lastFooterPx = -1;
  /** @type {Element|null} */
  #headerEl = null;
  /** @type {number} */
  #lockOpenUntil = 0;
  #isLockedOpen = () => Date.now() < this.#lockOpenUntil;
  #handleLockOpen = (event) => {
    try {
      const detail = (event && /** @type {CustomEvent} */(event).detail) || {};
      const ms = Number(detail && detail.ms != null ? detail.ms : 1200);
      const duration = Math.max(400, isFinite(ms) ? ms : 1200);
      this.#lockOpenUntil = Date.now() + duration;
      this.open();
    } catch (_) {}
  };

  // Ensure background remains scrollable, even if theme tries to lock it
  #unlockScroll = () => {
    try {
      const html = document.documentElement;
      const body = document.body;
      const removeClasses = ['scroll-lock', 'no-scroll', 'overflow-hidden', 'modal-open', 'lock-scroll'];
      removeClasses.forEach(c => body.classList.remove(c));
      // html?.style?.setProperty('overflow', 'auto', 'important');
      body?.style?.setProperty('overflow', 'auto', 'important');
      // html?.style?.setProperty('overscroll-behavior', 'auto', 'important');
      body?.style?.setProperty('overscroll-behavior', 'auto', 'important');
      body?.style?.setProperty('position', 'static', 'important');
      body?.style?.setProperty('width', 'auto', 'important');
    } catch (_) {}
  };

  #restoreScroll = () => {
    try {
      const html = document.documentElement;
      const body = document.body;
      ['overflow', 'overscroll-behavior'].forEach(prop => {
        html?.style?.removeProperty(prop);
        body?.style?.removeProperty(prop);
      });
      body?.style?.removeProperty('position');
      body?.style?.removeProperty('width');
    } catch (_) {}
  };

  // Close when clicking outside the drawer panel or on the backdrop
  #handleDocumentClick = (event) => {
    try {
      const dialog = this.#dialogEl;
      if (!dialog || !dialog.hasAttribute('open')) return;
      const panel = dialog.querySelector('.cart-drawer__inner');
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      // If click is anywhere inside the cart drawer component (including the open button), ignore
      if (this.contains(target)) return;
      // If click is inside the panel, ignore; otherwise close
      if (panel && panel.contains(target)) return;
      if (!this.#isLockedOpen()) this.close();
    } catch (_) {}
  };

  // Close on Escape when open
  #handleKeydown = (event) => {
    try {
      if (event.key !== 'Escape') return;
      const dialog = this.#dialogEl;
      if (!dialog || !dialog.hasAttribute('open')) return;
      if (!this.#isLockedOpen()) this.close();
      // prevent page-global handlers from double handling
      event.stopPropagation();
    } catch (_) {}
  };

  /**
   * Measure the site header and set a CSS var so the drawer panel starts below it.
   * Works across themes by checking several common selectors.
   */
  #setHeaderHeightVar = () => {
    try {
      const selectors = [
        'header.header',
        '.header',
        'header',
        '.site-header',
        '#shopify-section-header',
        '.shopify-section-header',
        '.header__wrapper',
        '.announcement-bar',
        '#shopify-section-announcement-bar',
        '.shopify-section-announcement-bar',
        '#preview-bar-iframe',
        '#preview-bar',
        'iframe#preview-bar-iframe'
      ];
      const nodes = selectors.flatMap(sel => Array.from(document.querySelectorAll(sel)));
      const dpr = window.devicePixelRatio || 1;
      const epsilon = 1 / dpr; // sub-pixel jitter tolerance
      const includeTol = 2;  // only elements truly touching the top count
      const mergeTol = 6;    // allow merging across small visual gaps (borders/shadows)
      const clampTol = 2;    // only clamp to primary header when it's within 2px of top
      // Build candidate intervals at the right edge
      const minCandidateHeight = 8; // include short announcement bars; ignore hairlines
      /** @type {{top:number,bottom:number}[]} */
      const intervals = [];
      for (const el of nodes) {
        const rect = el?.getBoundingClientRect?.();
        if (!rect) continue;
        const cs = window.getComputedStyle(el);
        const pos = cs?.position || '';
        const n = (el.nodeName || '').toLowerCase();
        const idc = `${el.id || ''} ${(el.className || '').toString()}`;
        const isFixedSticky = /fixed|sticky/i.test(pos);
        const isPreviewCandidate = /preview/i.test(idc) || (n === 'iframe' && /preview/i.test(idc));
        const likelyHeader = (isFixedSticky && (/(header|announcement|banner|preview)/i.test(idc) || n === 'header')) || isPreviewCandidate;
        // Only consider visible, positive-height segments that touch the top region
        const visible = cs.display !== 'none' && cs.visibility !== 'hidden' && (parseFloat(cs.opacity || '1') > 0.05);
        const touchesTop = rect.top <= includeTol && rect.bottom > 0;
        // Require intersection with right half of viewport to avoid counting small or left-only elements
        const spansRightHalf = (rect.right >= (window.innerWidth * 0.5)) && (rect.width >= Math.min(window.innerWidth, 320) * 0.5);
        if (likelyHeader && visible && touchesTop && spansRightHalf && rect.height >= minCandidateHeight) {
          intervals.push({ top: Math.max(0, rect.top), bottom: Math.max(0, rect.bottom) });
        }
      }
      // Merge from top: compute contiguous coverage starting at y=0
      intervals.sort((a, b) => a.top - b.top || b.bottom - a.bottom);
      let coverEnd = 0;
      let progressed = true;
      while (progressed) {
        progressed = false;
        for (const seg of intervals) {
          if (seg.top <= coverEnd + mergeTol && seg.bottom > coverEnd + epsilon) {
            if (seg.bottom > coverEnd) {
              coverEnd = seg.bottom;
              progressed = true;
            }
          }
        }
      }
      // No fallback: if nothing is touching the top, header offset is 0 (prevents creating a fake gap)
      // Safety: ensure we never go below the primary header bottom, but only if it currently touches the top
      const primaryHeader = document.querySelector('#shopify-section-header, .shopify-section-header, header.header, .header, header, .site-header');
      if (primaryHeader) {
        const hr = primaryHeader.getBoundingClientRect();
        const hcs = window.getComputedStyle(primaryHeader);
        const hPos = hcs?.position || '';
        const hVisible = hcs.display !== 'none' && hcs.visibility !== 'hidden' && (parseFloat(hcs.opacity || '1') > 0.05);
        if (/(fixed|sticky)/i.test(hPos) && hr && hr.height > 0 && hVisible && hr.top <= clampTol && hr.bottom > 0) {
          coverEnd = Math.max(coverEnd, Math.max(0, hr.bottom));
        }
      }
      let snapped = Math.max(0, Math.round(coverEnd * dpr) / dpr); // stacked bars + header contiguous from top
      if (this.#lastHeaderPx < 0 || Math.abs(this.#lastHeaderPx - snapped) > 0.5) {
        // Update CSS var and explicit inline top for maximum robustness across browsers
        this.#dialogEl?.style?.setProperty('--header-height', `${snapped}px`);
        if (this.#dialogEl?.style) this.#dialogEl.style.top = `${snapped}px`;
        this.#lastHeaderPx = snapped;
      }
    } catch (_) {
      // no-op; fallback stays at 60px
    }
  };

  /** Measure footer height so drawer stops above it. */
  #setFooterHeightVar = () => {
    try {
      const selectors = [
        'footer.footer',
        'footer',
        '.site-footer',
        '#shopify-section-footer',
        '.footer',
        '.shopify-section-footer'
      ];
      const nodes = selectors.flatMap(sel => Array.from(document.querySelectorAll(sel)));
      let footerTopFromViewportBottom = 0; // distance we must keep free at bottom
      for (const el of nodes) {
        const rect = el?.getBoundingClientRect?.();
        if (!rect) continue;
        // If footer is visible in viewport, the space it occupies at bottom is max(0, viewportBottom - rect.top)
        const overlap = Math.max(0, window.innerHeight - rect.top);
        footerTopFromViewportBottom = Math.max(footerTopFromViewportBottom, overlap);
      }
      const dpr = window.devicePixelRatio || 1;
      const snapped = Math.max(0, Math.round(footerTopFromViewportBottom * dpr) / dpr);
      if (this.#lastFooterPx < 0 || Math.abs(this.#lastFooterPx - snapped) > 0.25) {
        // Update CSS var and explicit inline bottom for maximum robustness across browsers
        this.#dialogEl?.style?.setProperty('--footer-height', `${snapped}px`);
        if (this.#dialogEl?.style) this.#dialogEl.style.bottom = `${snapped}px`;
        this.#lastFooterPx = snapped;
      }
    } catch (_) {
      // no-op; default stays 0px
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.#setHeaderHeightVar();
      this.#setFooterHeightVar();
      // Avoid native dialog top-layer; toggle attribute instead so page can scroll
      this.#dialogEl?.setAttribute?.('open', '');
      this.#unlockScroll();
      document.body?.setAttribute?.('data-cart-drawer-open', 'true');
      // No per-frame loop; updates are throttled on scroll/resize only
    }
  };

  

  open() {
    // Lifting lock when opening is fine; lock only prevents close
    this.#setHeaderHeightVar();
    this.#setFooterHeightVar();
    // Avoid native dialog top-layer; toggle attribute instead so page can scroll
    this.#dialogEl?.setAttribute?.('open', '');
    this.#unlockScroll();
    document.body?.setAttribute?.('data-cart-drawer-open', 'true');
    // No per-frame loop; updates are throttled on scroll/resize only
  }

  close() {
    if (this.#isLockedOpen()) return;
    this.#dialogEl?.removeAttribute?.('open');
    this.#restoreScroll();
    document.body?.removeAttribute?.('data-cart-drawer-open');
    // No per-frame loop to stop
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

//cart upsell

document.addEventListener('DOMContentLoaded', function(){

  document.addEventListener('change', function(e){
    if(!e.target.classList.contains('upsell-variant')) return;

    const select = e.target;
    const card = select.closest('.upsell-card');
    const priceEl = card.querySelector('[data-price]');
    const selectedOption = select.options[select.selectedIndex];

    priceEl.innerText = selectedOption.dataset.price;

    const btn = card.querySelector('.upsell-add');
    btn.dataset.variant = select.value;
  });

  document.addEventListener('click', function(e){
    if(!e.target.classList.contains('upsell-add')) return;

    const btn = e.target;
    const variantId = btn.dataset.variant;

    btn.innerText = '...';
    btn.disabled = true;
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: 1
      })
    })
    .then(() => {

      const cartComponent = document.querySelector('cart-items-component');

      if(cartComponent){
        const sectionId = cartComponent.dataset.sectionId;

        fetch(`/?section_id=${sectionId}`)
          .then(res => res.text())
          .then(html => {
            const temp = document.createElement('div');
            temp.innerHTML = html;

            const newComponent = temp.querySelector('cart-items-component');

            if(newComponent){
              cartComponent.innerHTML = newComponent.innerHTML;
            }
          });
      }

      btn.innerText = '✓';

      setTimeout(()=>{
        btn.innerText = 'Add';
        btn.disabled = false;
      }, 800);

    });
  });

});